import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheGrandEntrance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const heroHeading = "Timeless Elegance.";
  const heroSubtext = "Explore a world of rare provenance and exquisite craftsmanship. Nobilities Antiques brings history's finest treasures to your personal collection.";
  return <section id="the-grand-entrance" className="dark bg-background text-foreground relative h-[600px] w-full overflow-hidden flex items-center justify-center">
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569214/img/antique_gold_leaf_clock_moody.png)] bg-cover bg-center bg-fixed bg-no-repeat" aria-hidden="true" />

      <div className="absolute inset-0 bg-black/40 z-0" />

      <div ref={ref} className={`relative z-10 container mx-auto px-4 flex flex-col items-center text-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight drop-shadow-lg">
          {heroHeading}
        </h1>

        <p className="font-sans text-lg md:text-xl text-white/90 max-w-[600px] mb-10 leading-relaxed drop-shadow-md">
          {heroSubtext}
        </p>

        <div className="group">
          <Button asChild size="lg" className="bg-[var(--primary)] text-white hover:bg-[var(--primary)]/90 border-none px-8 py-6 text-lg font-semibold rounded-md shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(var(--primary),0.5)]">
            <Link to="/TheVaultPage">
              Discover the Collection
              <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}